import { apiFetch } from "../api/client";
import type { EventContext } from "../context/event";
import type { Field, Form, Option } from "./forms";

/**
 * LE FORMULAIRE, DANS LA LANGUE DU PARTICIPANT.
 *
 * ⚠️ IL NE L'ÉTAIT NULLE PART. L'écran affichait `field.label` et les libellés d'options
 * BRUTS, tels que l'API les rend : sur un parcours par ailleurs entièrement traduit,
 * l'écran où le participant écrit le plus restait en français, et changer la langue du
 * site ne le bougeait pas d'un mot.
 *
 * ⚠️ LE MAGASIN EST CELUI DU PRODUIT, PAS L'ESPACE DE NOMS DE LA V2. Les traductions de
 * formulaire vivent dans `LanguageEvents.translations`, sous les clés que la V1 lit
 * (`fo-meeting/src/components/FormBuilder.jsx`) et que le back office écrit déjà depuis
 * son écran dédié (`bo/src/pages/forms/v2/Translations.jsx`).
 *
 * C'est une entorse assumée à la règle du préfixe `FO_MEETING_V2_`. Cette règle existe
 * pour que la V2 ne réécrive pas le SITE de la V1 en essayant quelque chose ; ici il
 * s'agit du MÊME formulaire, déjà partagé par l'éditeur du panneau. Le ranger ailleurs
 * créerait deux traductions du même libellé, libres de diverger — exactement la
 * « seconde définition » que ce dépôt s'interdit partout ailleurs.
 *
 * La contrepartie est réelle : traduire depuis la V2 change aussi ce que la V1 sert.
 */

/**
 * ── LES TROIS FORMES DE CLÉS ──
 *
 * ⚠️ ELLES NE S'INVENTENT PAS : ce sont celles que la V1 lit et que le back office
 * écrit. En changer une, c'est écrire à côté de ce que les deux autres fronts liront —
 * une traduction faite ici resterait invisible, et vice versa.
 *
 * ⚠️ ET C'EST `modelId`, PAS `id`. `modelId` désigne la QUESTION (`Translations.id`),
 * `id` la pose dans ce formulaire (`FormModels.id`). Or la route du formulaire détruit
 * et réinsère à chaque enregistrement : les `FormModels.id` changent tous. Une clé
 * bâtie dessus perdrait sa traduction à la première sauvegarde du formulaire.
 */
export const fieldKey = (formId: number, modelId: number) =>
  `${formId}-${modelId}`;

/**
 * ⚠️ L'IDENTIFIANT DE L'OPTION, PAS SA CLÉ. Le back office bâtit
 * `${form.id}-${d.modelId}-${v.id}` et la V1 lit `${form.id}-${model.modelId}-${m.id}` :
 * les deux prennent `Translations.id`. Bâtie sur la clé d'option — « fQbnYib » au lieu
 * de « 23189 » — la traduction ecrite ici serait invisible des deux autres fronts, et
 * les leurs invisibles d'ici. Deux jeux de traductions du meme mot, sans que rien ne le
 * signale.
 */
export const optionKey = (formId: number, modelId: number, optionId: number) =>
  `${formId}-${modelId}-${optionId}`;

/** Un commentaire ou un bloc HTML : il n'a pas de question, donc pas de `modelId`. */
export const decorKey = (formId: number, uid: string) => `${formId}-fm-${uid}`;

/** Ce que `GET /languages` rend pour chaque langue, et que la V2 jetait. */
export type Translations = Record<number, Record<string, string>>;

/**
 * LIRE LES TRADUCTIONS DE CET ÉVÉNEMENT.
 *
 * ⚠️ AVEC `domainId` ET `eventId`, ET C'EST TOUT LE SUJET. Sans eux, la route ne rend
 * que `Languages.translations` — les libellés globaux du produit (`YES`, `BACK`,
 * `NEXT`…). Les traductions de formulaire vivent dans `LanguageEvents`, que la route ne
 * joint QUE si on lui nomme le domaine et l'événement. C'est pourquoi `fetchLanguages`
 * ne les voit pas : elle appelle la même route sans paramètre, et c'est très bien —
 * elle sert le CATALOGUE des langues, qui ne dépend d'aucun événement et se charge
 * avant lui. Deux lectures pour deux besoins, plutôt qu'une dépendance ajoutée à un
 * chemin de démarrage.
 */
export async function fetchTranslations(
  event: EventContext,
  signal?: AbortSignal,
): Promise<Translations> {
  const answer = await apiFetch<{
    languages?: Array<{ id?: number; translations?: Record<string, unknown> }>;
  }>(
    `/languages?domainId=${event.domainId}&eventId=${event.eventId}`,
    { api: "apix", signal },
  );

  const byLanguage: Translations = {};
  for (const l of answer?.languages ?? []) {
    if (typeof l?.id !== "number") continue;
    const own: Record<string, string> = {};
    for (const [key, value] of Object.entries(l.translations ?? {}))
      if (typeof value === "string") own[key] = value;
    byLanguage[l.id] = own;
  }
  return byLanguage;
}

/**
 * LA CASCADE DE REPLI — celle de la V1, à l'identique
 * (`fo-meeting/src/hooks/useTranslations.jsx`) : langue courante, puis langue par
 * défaut de l'événement, puis FR, puis la valeur brute.
 *
 * ⚠️ LE DERNIER REPLI EST LA VALEUR BRUTE, ET IL EST OBLIGATOIRE. La V1 finit sur
 * `` `[${key}]` `` quand elle ne trouve rien : un participant y lit « [5525-19312] » à
 * la place du nom du champ. Un libellé non traduit doit s'afficher dans sa langue
 * d'origine — imparfait, mais lisible. Jamais un identifiant technique.
 */
export function translated(
  translations: Translations,
  languages: { current: number; eventDefault: number; reference: number },
  key: string,
  raw: string,
): string {
  for (const id of [languages.current, languages.eventDefault, languages.reference]) {
    const value = translations[id]?.[key];
    if (typeof value === "string" && value.trim() !== "") return value;
  }
  return raw;
}

/**
 * LES MOTS D'UN FORMULAIRE, DÉJÀ RÉSOLUS — ce que l'écran consomme.
 *
 * Construit une fois par formulaire, plutôt que de laisser chaque champ rassembler la
 * langue courante, la langue par défaut, FR et le format des clés. `Row` ne sait alors
 * rien de tout ça : il demande un libellé, il en obtient un.
 */
export type Words = {
  field: (field: Field) => string;
  option: (field: Field, option: Option) => string;
  decor: (field: Field) => string;
};

export function wordsOf(
  form: Form,
  translations: Translations,
  languages: { current: number; eventDefault: number; reference: number },
): Words {
  const say = (key: string, raw: string) =>
    translated(translations, languages, key, raw);

  return {
    field: (f) =>
      f.modelId === null
        ? f.label
        : say(fieldKey(form.id, f.modelId), f.label),
    option: (f, o) =>
      f.modelId === null || o.id === null
        ? o.label
        : say(optionKey(form.id, f.modelId, o.id), o.label),
    decor: (f) => {
      const uid = uidOf(form, f);
      return uid ? say(decorKey(form.id, uid), f.content) : f.content;
    },
  };
}

/** Une chaîne du formulaire, avec la clé sous laquelle sa traduction se range. */
export type Translatable = { key: string; source: string };

/**
 * TOUT CE QU'UN FORMULAIRE DONNE À TRADUIRE — libellés de champs, libellés de
 * réponses, et le texte des décors.
 *
 * ⚠️ LES TROIS FAMILLES, comme la V1 et le back office. En couvrir moins laisserait la
 * moitié du formulaire en français, ce qui est précisément le défaut qu'on corrige :
 * un participant anglophone lisant « First name » au-dessus d'un « Oui / Non ».
 *
 * ⚠️ LA SOURCE EST `field.label`, DONC DÉJÀ `label || value` — le libellé propre au
 * formulaire d'abord, celui de la question ensuite. C'est ce que la V1 passe en
 * `valueDefault`, et c'est ce qui doit être traduit : traduire le libellé de la
 * question alors que le formulaire en impose un autre traduirait un texte que personne
 * ne voit.
 */
export function translatablesOf(form: Form): Translatable[] {
  const out: Translatable[] = [];

  for (const field of form.fields) {
    if (field.content.trim() !== "") {
      /* Un décor n'a pas de question : sa clé passe par l'`uid` que le back office lui
         pose dans `metadata` à la création. Sans `uid`, il n'est pas traduisible — et
         lui en inventer un ici le ferait diverger du back office. */
      const uid = uidOf(form, field);
      if (uid) out.push({ key: decorKey(form.id, uid), source: field.content });
      continue;
    }

    if (field.modelId === null) continue;

    if (field.label.trim() !== "")
      out.push({
        key: fieldKey(form.id, field.modelId),
        source: field.label,
      });

    for (const option of field.options)
      /* Sans identifiant, pas de cle de traduction possible — et en inventer une la
         ferait diverger de ce que le back office ecrit. */
      if (option.id !== null && option.label.trim() !== "")
        out.push({
          key: optionKey(form.id, field.modelId, option.id),
          source: option.label,
        });
  }

  return out;
}

/** L'`uid` du décor, tel que le back office l'a rangé dans `metadata`. */
function uidOf(form: Form, field: Field): string | null {
  const raw = form.models.find((m) => String(m.id) === field.id);
  const uid = raw?.metadata?.uid;
  return typeof uid === "string" && uid !== "" ? uid : null;
}

/**
 * ÉCRIRE LES TRADUCTIONS — une seule requête, toutes langues et toutes clés.
 *
 * ⚠️ UNE SEULE, ET C'EST LA ROUTE QUI L'IMPOSE. Elle fait un `JSON_MERGE_PATCH` par
 * langue sur la ligne `LanguageEvents` ; découper en une requête par clé lancerait
 * autant d'écritures concurrentes sur la MÊME ligne, et elles se perdraient
 * mutuellement. Groupées, elles fusionnent en une passe.
 *
 * La fusion est aussi ce qui rend l'écriture sûre : contrairement au `PUT` du
 * formulaire, qui détruit avant de réinsérer, on n'efface rien de ce qui est déjà là —
 * ni les traductions du produit, ni celles faites au back office.
 */
export async function writeTranslations(
  event: EventContext,
  byLanguage: Map<number, Map<string, string>>,
  signal?: AbortSignal,
): Promise<void> {
  const languages: Record<string, Record<string, string>> = {};
  for (const [languageId, values] of byLanguage) {
    if (values.size === 0) continue;
    languages[String(languageId)] = Object.fromEntries(values);
  }
  if (Object.keys(languages).length === 0) return;

  await apiFetch("/languages/translations", {
    api: "apix",
    method: "PUT",
    body: { domainId: event.domainId, eventId: event.eventId, languages },
    signal,
  });
}
